import { z } from 'zod';

export const ListWhatsAppInboxChatsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const ListWhatsAppInboxMessagesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  afterTimestamp: z.coerce.number().int().min(0).optional(),
  downloadMedia: z.coerce.boolean().optional(),
});

export const WhatsAppInboxChatSchema = z.object({
  id: z.string(),
  picture: z.string().nullable(),
  name: z.string().nullable(),
  lastMessage: z
    .object({
      body: z.string().nullable(),
      timestamp: z.number(),
      fromMe: z.boolean().optional(),
    })
    .nullable(),
  unreadCount: z.number(),
});

export const WhatsAppInboxMessageSchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  from: z.string(),
  fromMe: z.boolean(),
  source: z.string().nullable(),
  to: z.string(),
  participant: z.string().nullable(),
  body: z.string().nullable(),
  hasMedia: z.boolean(),
  media: z
    .object({
      url: z.string(),
      mimetype: z.string(),
      filename: z.string(),
    })
    .nullable(),
  mediaUrl: z.string().nullable(),
  ack: z.number().nullable(),
  ackName: z.string().nullable(),
  author: z.string().nullable(),
  location: z.unknown().nullable(),
  vCards: z.array(z.unknown()).nullable(),
  replyTo: z.unknown().nullable(),
});

export const WhatsAppInboxChatsResponseSchema = z.object({
  chats: z.array(WhatsAppInboxChatSchema),
});

export const WhatsAppInboxMessagesResponseSchema = z.object({
  messages: z.array(WhatsAppInboxMessageSchema),
});

export const SendWhatsAppInboxMessageBodySchema = z
  .object({
    text: z.string().trim().min(1).max(4000).optional(),
    media: z
      .object({
        storageKey: z.string().trim().min(1),
        caption: z.string().trim().max(2000).optional(),
        filename: z.string().trim().min(1).max(180).optional(),
        contentType: z.string().trim().min(1).max(127).optional(),
      })
      .optional(),
  })
  .refine((body) => body.text !== undefined || body.media !== undefined, {
    message: 'text or media is required',
  })
  .refine((body) => !(body.text !== undefined && body.media !== undefined), {
    message: 'text and media are mutually exclusive',
  });

export const SendWhatsAppInboxMessageResponseSchema = z.object({
  providerMessageId: z.string().nullable(),
});

export const EditWhatsAppInboxMessageBodySchema = z.object({
  text: z.string().trim().min(1).max(4000),
});

export const PresignWhatsAppInboxAttachmentBodySchema = z.object({
  contentType: z.string().trim().min(1),
  contentLength: z.number().int().min(1),
});

export const PresignWhatsAppInboxAttachmentResponseSchema = z.object({
  uploadUrl: z.url(),
  storageKey: z.string(),
  expiresAt: z.string(),
});

export type ListWhatsAppInboxChatsQuery = z.infer<typeof ListWhatsAppInboxChatsQuerySchema>;
export type ListWhatsAppInboxMessagesQuery = z.infer<typeof ListWhatsAppInboxMessagesQuerySchema>;
export type WhatsAppInboxChat = z.infer<typeof WhatsAppInboxChatSchema>;
export type WhatsAppInboxMessage = z.infer<typeof WhatsAppInboxMessageSchema>;
export type WhatsAppInboxChatsResponse = z.infer<typeof WhatsAppInboxChatsResponseSchema>;
export type WhatsAppInboxMessagesResponse = z.infer<typeof WhatsAppInboxMessagesResponseSchema>;
export type SendWhatsAppInboxMessageBody = z.infer<typeof SendWhatsAppInboxMessageBodySchema>;
export type SendWhatsAppInboxMessageResponse = z.infer<
  typeof SendWhatsAppInboxMessageResponseSchema
>;
export type EditWhatsAppInboxMessageBody = z.infer<typeof EditWhatsAppInboxMessageBodySchema>;
export type PresignWhatsAppInboxAttachmentBody = z.infer<
  typeof PresignWhatsAppInboxAttachmentBodySchema
>;
export type PresignWhatsAppInboxAttachmentResponse = z.infer<
  typeof PresignWhatsAppInboxAttachmentResponseSchema
>;
